import { useCallback, useEffect, useMemo, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { APP_ITEMS, DEFAULT_FAVORITE_IDS } from './appRegistry';
import {
  isStreamPlayerUnlocked,
  STREAM_PLAYER_UNLOCK_EVENT,
  STREAM_PLAYER_UNLOCK_LS_KEY,
} from '../streamPlayerUnlock';

export const VORAKIT_FAVORITE_APPS_KEY = 'vorakit_favorite_apps';
const LEGACY_MOST_USED_KEY = 'vorakit_most_used_apps';

function validateIds(rawIds) {
  if (!Array.isArray(rawIds)) return [];
  return rawIds
    .map((id) => {
      if (id === 'screenshot-annotator') return 'image-marker';
      if (id === 'floor-planner') return 'flowspace';
      return id;
    })
    .filter((id) => typeof id === 'string' && APP_ITEMS.some((a) => a.id === id))
    .slice(0, 4);
}

function readLegacyMostUsedIds() {
  try {
    const raw = localStorage.getItem(LEGACY_MOST_USED_KEY);
    if (raw === null) return null;
    const data = JSON.parse(raw);
    if (!Array.isArray(data)) return null;
    const ids = data.map((x) => (typeof x === 'string' ? x : x?.id)).filter(Boolean);
    return validateIds(ids);
  } catch {
    return null;
  }
}

/** No `vorakit_favorite_apps` key → defaults until user edits. Legacy key migrated once. */
function readInitialIds() {
  try {
    const raw = localStorage.getItem(VORAKIT_FAVORITE_APPS_KEY);
    if (raw !== null) {
      return validateIds(JSON.parse(raw));
    }
    const migrated = readLegacyMostUsedIds();
    if (migrated !== null && migrated.length > 0) {
      localStorage.setItem(VORAKIT_FAVORITE_APPS_KEY, JSON.stringify(migrated));
      return migrated;
    }
    return [...DEFAULT_FAVORITE_IDS];
  } catch {
    return [...DEFAULT_FAVORITE_IDS];
  }
}

function readFavoriteKeyOnly() {
  try {
    const raw = localStorage.getItem(VORAKIT_FAVORITE_APPS_KEY);
    if (raw === null) return [...DEFAULT_FAVORITE_IDS];
    return validateIds(JSON.parse(raw));
  } catch {
    return [...DEFAULT_FAVORITE_IDS];
  }
}

function persist(ids) {
  try {
    localStorage.setItem(VORAKIT_FAVORITE_APPS_KEY, JSON.stringify(ids));
  } catch {
    /* quota */
  }
}

export function useFavoriteApps() {
  const [ids, setIds] = useState(readInitialIds);
  const [streamUnlocked, setStreamUnlocked] = useState(() => isStreamPlayerUnlocked());

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key && e.key !== VORAKIT_FAVORITE_APPS_KEY) return;
      setIds(readFavoriteKeyOnly());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const syncUnlock = () => setStreamUnlocked(isStreamPlayerUnlocked());
    window.addEventListener(STREAM_PLAYER_UNLOCK_EVENT, syncUnlock);
    const onStorage = (e) => {
      if (e.key === STREAM_PLAYER_UNLOCK_LS_KEY || e.key === null) syncUnlock();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(STREAM_PLAYER_UNLOCK_EVENT, syncUnlock);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const visibleFavoriteIds = useMemo(
    () => (streamUnlocked ? ids : ids.filter((id) => id !== 'stream-player')),
    [ids, streamUnlocked],
  );

  const favoriteApps = useMemo(
    () => visibleFavoriteIds.map((id) => APP_ITEMS.find((a) => a.id === id)).filter(Boolean),
    [visibleFavoriteIds],
  );

  const favoriteIdSet = useMemo(() => new Set(ids), [ids]);
  const visibleFavoriteCount = streamUnlocked ? ids.length : ids.filter((id) => id !== 'stream-player').length;
  const isFull = visibleFavoriteCount >= 4;

  const reorder = useCallback((activeId, overId) => {
    setIds((prev) => {
      const unlocked = isStreamPlayerUnlocked();
      const visible = unlocked ? prev : prev.filter((x) => x !== 'stream-player');
      const oldIndex = visible.indexOf(activeId);
      const newIndex = visible.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
      const movedVisible = arrayMove(visible, oldIndex, newIndex);
      if (unlocked || !prev.includes('stream-player')) {
        persist(movedVisible);
        return movedVisible;
      }
      let vi = 0;
      const next = prev.map((id) => (id === 'stream-player' ? id : movedVisible[vi++]));
      persist(next);
      return next;
    });
  }, []);

  const addToFavorite = useCallback((id) => {
    setIds((prev) => {
      if (prev.includes(id)) return prev;
      if (prev.length < 4) {
        const next = [...prev, id];
        persist(next);
        return next;
      }
      if (!isStreamPlayerUnlocked() && prev.includes('stream-player') && id !== 'stream-player') {
        const next = prev.map((x) => (x === 'stream-player' ? id : x));
        persist(next);
        return next;
      }
      return prev;
    });
  }, []);

  const removeFromFavorite = useCallback((id) => {
    setIds((prev) => {
      const next = prev.filter((x) => x !== id);
      persist(next);
      return next;
    });
  }, []);

  return {
    ids,
    visibleFavoriteIds,
    favoriteApps,
    favoriteIdSet,
    isFull,
    reorder,
    addToFavorite,
    removeFromFavorite,
  };
}
